import { zodResolver } from "@hookform/resolvers/zod";
import { HeaderForm } from "@/components/header-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { SimpleAccordion } from "@/components/ui/simple-accordion";
import { useForm } from "react-hook-form";
import { ProjectAboutFormData, projectAboutFormSchema } from "./schema";
import { ProjectAboutForm } from "./project-about-form";
import { useCreateProject, useUpdateProject } from "@/services/queries/useProject";
import { ProjectAbout as ProjectAboutClass, Project } from "@/types/project";
import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { useAuthContext } from "@/context/authentication/auth-context";

interface ProjectAboutProps {
  onContinue?: () => void;
  onBack?: () => void;
  setProjectId: (id: number) => void;
  initialData?: any; // Start with any to avoid type issues, then refine if needed. Or import Project type.
}

export function ProjectAbout({
  onContinue,
  onBack,
  setProjectId,
  initialData
}: ProjectAboutProps) {

  const { person } = useAuthContext()

  const [pending, setPending] = useState(false);

  const form = useForm<ProjectAboutFormData>({
    resolver: zodResolver(projectAboutFormSchema),
    defaultValues: {
      title: "",
      proponent: person?.name,
      genre: "",
      salicNumber: "",
      cnpjProponent: person?.juridic_person?.document || "",
      releaseDate: "",
      episodeCount: "",
      episodeDuration: "",
      segment: "",
      format: "",
      logline: "",
    },
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        title: initialData.name || initialData.title || "",
        proponent: initialData.proponent || person?.name || "",
        genre: initialData.genres?.[0]?.genre?.description || initialData.genre || "",
        salicNumber: initialData.salic_number || "",
        cnpjProponent: initialData.cnpj_proponent || person?.document || "",
        releaseDate: initialData.release_date?.split("T")[0] || "",
        episodeCount: initialData.episode_count || "",
        episodeDuration: initialData.episode_duration || "",
        segment: initialData.segments?.[0]?.segment?.description || initialData.segment || "",
        format: initialData.movie_format || initialData.format || "",
        logline: initialData.logline || "",
      });
      // Also update the project ID in parent state if needed, though it's likely already set by the parent
      if (initialData.id) {
        setProjectId(initialData.id);
      }
    }
  }, [initialData, form, person, setProjectId]);

  const { mutateAsync: createProject, isPending: isCreating } = useCreateProject(false);
  const { mutateAsync: updateProject, isPending: isUpdating } = useUpdateProject(false);

  const onFormSubmit = async (data: ProjectAboutFormData) => {
    setPending(true);
    try {
      if (initialData?.id) {
        await updateProject(ProjectAboutClass.mapToProjectAbout(data, initialData.id));
        setProjectId(initialData.id);
      } else {
        await createProject(ProjectAboutClass.mapToProjectAbout(data))
          .then(res => {
            setProjectId(res?.data.id);
          })
      }

      toast({
        title: "Dados salvos com sucesso",
        variant: "success",
      });

      onContinue?.()
    } catch (error) {
      console.error('Erro ao salvar dados:', error);
      toast({
        title: "Erro ao salvar dados",
        variant: "destructive",
      });
    } finally {
      setPending(false);
    }
  };

  const accordionItems = [
    {
      name: "SOBRE O PROJETO",
      component: <ProjectAboutForm form={form} />,
    },
  ];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className="space-y-8">
        <SimpleAccordion
          className="bg-darkblue rounded-lg p-8"
          itemClassName="border-none"
          items={accordionItems.map((item) => ({
            id: item.name,
            title: <HeaderForm title={item.name} />,
            content: item.component
          }))}
        />

        {/* Navigation Buttons */}
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            type="submit"
            size={"sm"}
            disabled={pending || isCreating || isUpdating}
            isLoading={pending || isCreating || isUpdating}
            children="Voltar"
            className="px-8"
          />

          <Button
            type="submit"
            size={"sm"}
            disabled={pending || isCreating || isUpdating}
            isLoading={pending || isCreating || isUpdating}
            children="Salvar & Continuar"
            className="px-8"
          />
        </div>
      </form>
    </Form>
  )
}